import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, IconButton, Button } from '@mui/material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import ChecklistIcon from '@mui/icons-material/Checklist';

import serviceMonitorService from '../../service/serviceMonitor.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

dayjs.extend(relativeTime);
dayjs.extend(duration);

const ServiceMonitorCard = ({
  data,
  setRefetch,
  rows,
  params,
  helperData,
  autoCompleteOptions,
  setConfirmDialogData,
  hideConfirmDialog,
  setReportDialogData,
}) => {
  const [elapsedTime, setElapsedTime] = useState(null);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    if (data.osr_startDate) {
      if (timer) {
        clearInterval(timer);
      }

      const interval = setInterval(() => {
        const start = dayjs(data.osr_startDate);
        const now = dayjs();
        const diff = dayjs.duration(now.diff(start));

        setElapsedTime(
          `${diff.hours().toString().padStart(2, '0')}:${diff.minutes().toString().padStart(2, '0')}:${diff.seconds().toString().padStart(2, '0')}`
        );
      }, 1000);

      setTimer(interval);

      return () => clearInterval(interval);
    } else {
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
      setElapsedTime(null);
    }
  }, [data.osr_startDate]);

  const handleStartClick = () => {
    serviceMonitorService.startJob({ serviceTaskId: data.st_id }).then(() => {
      setRefetch(true);
      addNotification({
        content: 'A művelet elindítva',
        type: NotificationType.SUCCESS,
      });
      hideConfirmDialog();
    });
  };

  const handleStopClick = (fromChkButton = false) => {
    setReportDialogData({
      open: true,
      selectedTaskId: data.st_id,
      reportId: fromChkButton ? data.osr_id ?? null : null,
    });
    hideConfirmDialog();
    /*serviceMonitorService.stopJob({ serviceTaskId: data.st_id }).then(() => {
      setRefetch(true);
      addNotification({
        content: 'A művelet leállítva',
        type: NotificationType.WARNING,
      });
      hideConfirmDialog();
    });*/
  };

  const handleOpenInterruptionDialog = () => {
    setConfirmDialogData({
      open: true,
      title: `Adja meg a megszakítás okát`,
      message: '',
      buttons: helperData?.interruption?.map((reason) => ({
        label: reason.name,
        variant: 'contained',
        color: '#3bc3f5',
        onClick: () => {
          serviceMonitorService
            .interruptJob({
              serviceTaskId: data.st_id,
              interruptionCode: reason.code,
            })
            .then(() => {
              // Handle success

              addNotification({
                content: 'A művelet megszakítva',
                type: NotificationType.WARNING,
              });
              setRefetch(true);
            })
            .finally(() => {
              hideConfirmDialog();
            });
        },
      })),
    });
  };

  const openConfirmDialog = () => {
    const validStatuses = ['INPROGRESS', 'INTERRUPTED', 'APPROVED'];

    if (!validStatuses.includes(data.serviceStatusId)) {
      return addNotification({
        content: 'A művelet már készre van jelentve.',
        type: NotificationType.WARNING,
      });
    }

    if (data.serviceStatusId === 'INPROGRESS') {
      setConfirmDialogData({
        open: true,
        message: 'Készre jelenti?',
        buttons: [
          {
            label: 'Kész',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => handleStopClick(),
          },
          {
            label: 'Megszakít',
            variant: 'contained',
            color: '#f59e0b',
            onClick: () => handleOpenInterruptionDialog(),
          },
          /* 
          {
            label: 'Állásidő',
            variant: 'contained',
            color: '#0076f8',
            onClick: () => handleStopClick(),
          },
          */
        ],
      });
    } else {
      setConfirmDialogData({
        open: true,
        message: 'Biztosan el szeretné indítani a műveletet?',
        buttons: [
          {
            label: 'Igen',
            variant: 'contained',
            color: '#27ca37',
            onClick: () => handleStartClick(),
          },
        ],
      });
    }
  };

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          borderRadius: 2,
          background: '#E4F0FF',
        }}
      >
        <Grid
          container
          direction="row"
          sx={{
            justifyContent: 'center',
            alignItems: 'stretch',
          }}
        >
          <Grid
            item
            xs={12}
            sx={{
              backgroundColor: data.st_statusColor,
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                borderRadius: 2,
                boxShadow:
                  ' rgba(6, 24, 44, 0.4) 0px 0px 0px 2px, rgba(6, 24, 44, 0.65) 0px 4px 6px -1px, rgba(255, 255, 255, 0.08) 0px 1px 0px inset;',
                padding: 1,
                height: '100%',
                cursor: 'pointer',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  backgroundColor: '#555',
                },

                '&:hover': {
                  background: 'rgba(0, 0, 0, 0.15);',
                },
              }}
              onClick={() => openConfirmDialog()}
            >
              {data.osr_startDate && data.serviceStatusId === 'INPROGRESS' && (
                <Grid container spacing={0}>
                  <Grid item xs={7}>
                    <Typography component="span" display="inline" fontWeight="bold">
                      Elindítva:{' '}
                    </Typography>
                    <Typography component="span" display="inline">
                      {dayjs(data.osr_startDate).format('YYYY-MM-DD HH:mm')}
                    </Typography>
                  </Grid>

                  <Grid item xs={5} textAlign="right">
                    <Typography fontWeight="bold" color="blue">
                      {elapsedTime}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={0}>
                <Grid item xs={11} container spacing={0}>
                  <Grid item xs={12}>
                    <Typography fontWeight="bold" fontStyle="italic" textAlign={'center'} fontSize={'1.3em'}>
                      {data.operationName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography fontWeight="bold" fontStyle="italic" textAlign={'center'}>
                      {data.s_projectName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography fontWeight="bold" fontStyle="italic" textAlign={'center'}>
                      {data.note}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item xs={1}>
                  {data.serviceStatusId === 'INPROGRESS' && (
                    <Button
                      sx={{
                        mt: 1,
                        p: 0.5,
                        minWidth: 0,
                        width: '36px',
                        color: 'grey.600',
                        backgroundColor: 'grey.200',
                        '&:hover': {
                          backgroundColor: 'grey.400',
                        },
                        border: '1px solid',
                        borderColor: 'grey.600',
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleStopClick(true);
                      }}
                    >
                      <ChecklistIcon
                        sx={{
                          fontSize: 30,
                        }}
                      />
                    </Button>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={0}>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontWeight="bold" fontStyle="italic">
                    {data.s_workingNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontStyle="italic">{data.s_projectOrderNumber}</Typography>
                </Grid>
              </Grid>
              <Grid container spacing={0} mt="0.5rem">
                <Grid item xs={4}>
                  <Typography variant="caption" fontWeight="bold">
                    Kezdés
                  </Typography>
                  <Typography>{dayjs(data.startDate).format('YYYY-MM-DD')}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" fontWeight="bold">
                    Befejezés
                  </Typography>
                  <Typography>{dayjs(data.BefejezesiHatarido).format('YYYY-MM-DD')}</Typography>
                </Grid>
                <Grid item xs={4} p={0.5}>
                  <Typography fontWeight="bold">{data.resourceoneName || ''}</Typography>
                </Grid>
                <Grid item xs={12} p={0.5} textAlign={'center'}>
                  {data.osr_interruptionCode && (
                    <Typography fontWeight="bold" sx={{ background: '#FF6363', borderRadius: '8px' }}>
                      Megszakítás oka: {data.osr_interruptionName}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

export default ServiceMonitorCard;
